import { Navigate, useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { authAtom } from '../../store/auth';
import { useEffect } from 'react';
import { isTokenExpiredOrExpiringSoon, storeRedirectInfo } from '../../utils/auth';
import { UserRole } from '../../constants/roles';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  requireApprover?: boolean;
}

export const ProtectedRoute = ({ 
  children, 
  allowedRoles = [UserRole.USER, UserRole.ADMIN],
  requireApprover = false
}: ProtectedRouteProps) => {
  const { isAuthenticated, user, token } = useAtomValue(authAtom);
  const location = useLocation();

  // Check if token is expired on route change
  useEffect(() => {
    if (isAuthenticated && token && isTokenExpiredOrExpiringSoon(token)) {
      // Update auth state will happen via API calls that will trigger 401
      console.debug('Token is expired or expiring soon on protected route');
    }
  }, [isAuthenticated, token, location.pathname]);

  if (!isAuthenticated) {
    // Store current path for redirect after login
    if (location.pathname !== '/login') {
      storeRedirectInfo(
        location.pathname + location.search,
        'Please log in to access this page'
      );
    }
    return <Navigate to="/login" replace />;
  }

  const userRole = (user?.role || UserRole.USER) as UserRole;
  
  // ADMIN role has access to everything
  if (userRole === UserRole.ADMIN) {
    return <>{children}</>;
  }

  // Check if user's role is in the allowed roles list
  if (!allowedRoles.includes(userRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check if approver status is required
  if (requireApprover && user && !user.isApprover) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}; 